import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import {
  CreateQcTestResultDto,
  CustomQualityControlDto,
  CustomQualityControlType,
  HotLabEquipmentDto,
  QCResult,
  QCType,
  QualityControlAssignmentDto,
  VisualQualityControlDto,
} from '@services/dtos'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { isNil } from 'lodash'
import { MemberDto, MemberService } from '@services/api'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import { AddWrapperInput } from '../inputs/AddWrapperInput'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { HotLabEquipmentTypeLabels, MemberRoleEnumLabels, VisualQualityControlTypeLabels } from '@utils/constants'
import { hotLabEquipmentService } from '@services/hotLabEquipmentService'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import { DateTimePicker } from '@mui/x-date-pickers-pro'
import moment from 'moment'
import { FormHeader } from '../forms/FormHeader'
import { UpTransition } from '../animations'
import {
  saveGeneratorElutionQcTestResult,
  savePreparationLotQcTestResult,
} from '@state/reducers/operationQualityControlsReducer'
import InputField from '../inputs/InputField'
import SelectField from '../inputs/SelectField'
import { SimpleFileInput } from '../inputs/SimpleFileInput'
import { getEquipmentTypes, getQualityControlExpectedLabel } from '@utils/qualityControl.utils'
import { qcTestResultSchema } from '@utils/schemas'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import HotLabEquipmentForm from '../forms/HotLabEquipmentForm'

export function computeAutoResult(
  type: QCType,
  value: string,
  opts: {
    phMin?: number | null
    phMax?: number | null
    radiochemicalPurityPercent?: number | null
    radionuclidicPurityPercent?: number | null
    customMin?: number | null
    customMax?: number | null
  }
): QCResult | null {
  switch (type) {
    case QCType.PH: {
      if (!value) {
        return null
      }
      const num = parseFloat(value)
      if (isNaN(num)) {
        return null
      }
      const passMin = isNil(opts.phMin) || num >= opts.phMin
      const passMax = isNil(opts.phMax) || num <= opts.phMax
      return passMin && passMax ? QCResult.PASS : QCResult.FAIL
    }
    case QCType.CHEMICAL_PURITY: {
      if (!value || isNil(opts.radiochemicalPurityPercent)) {
        return null
      }
      return parseFloat(value) >= opts.radiochemicalPurityPercent ? QCResult.PASS : QCResult.FAIL
    }
    case QCType.RADIONUCLIDIC_PURITY: {
      if (!value || isNil(opts.radionuclidicPurityPercent)) {
        return null
      }
      return parseFloat(value) >= opts.radionuclidicPurityPercent ? QCResult.PASS : QCResult.FAIL
    }
    case QCType.CUSTOM: {
      if (!value) {
        return null
      }
      const num = parseFloat(value)
      if (isNaN(num)) {
        return null
      }
      const passMin = isNil(opts.customMin) || num >= opts.customMin
      const passMax = isNil(opts.customMax) || num <= opts.customMax
      return passMin && passMax ? QCResult.PASS : QCResult.FAIL
    }
    default:
      return null
  }
}

export function ResultChip({ result }: { result: QCResult | null }) {
  if (result === QCResult.PASS) {
    return (
      <Chip
        icon={<CheckCircleOutlineIcon />}
        label="Conforme"
        color="success"
        size="small"
        variant="outlined"
      />
    )
  }
  if (result === QCResult.FAIL) {
    return (
      <Chip
        icon={<CancelOutlinedIcon />}
        label="Non conforme"
        color="error"
        size="small"
        variant="outlined"
      />
    )
  }
  return <Chip label="N/A" size="small" variant="outlined" />
}

export function getRecordTooltip({
  kind,
  visualQualityControl,
  customQualityControl,
}: {
  kind: QCType
  visualQualityControl?: VisualQualityControlDto | null
  customQualityControl?: CustomQualityControlDto | null
}): string {
  const base = 'Enregistrer : '
  switch (kind) {
    case QCType.VISUAL:
      return `${base}${visualQualityControl?.type
        ? VisualQualityControlTypeLabels[visualQualityControl.type]
        : ''}`
    case QCType.PH:
      return `${base}pH`
    case QCType.CHEMICAL_PURITY:
      return `${base}Pureté radiochimique`
    case QCType.RADIONUCLIDIC_PURITY:
      return `${base}Pureté radionuclidique`
    case QCType.CUSTOM:
      return `${base}${customQualityControl?.label ?? ''}`
    default:
      return `${base}N/A`
  }
}

interface FormValues {
  observedNumericValue: number
  observedTextValue: string
  observedBooleanValue?: boolean
  selectedOptionId: string
  observedDiffers: boolean
  measuredAt: Date | null
  notes: string
  hotLabEquipmentId?: string | null
}

interface Props {
  open: boolean
  onClose: () => void
  entityType: 'preparationLot' | 'generatorElution'
  entityId: string
  kind: QCType
  visualQualityControl: VisualQualityControlDto | null
  customQualityControl: CustomQualityControlDto | null
  assignment: QualityControlAssignmentDto
}

export function RecordQcTestResultDialog({
  open,
  onClose,
  entityType,
  entityId,
  kind,
  visualQualityControl,
  customQualityControl,
  assignment,
}: Props) {
  const dispatch = useAppDispatch()
  const user = useAppSelector(({ auth }) => auth.user)
  const submitting = useAppSelector(({ operationQualityControls }) => operationQualityControls.submitting)

  const defaultMember = user && 'role' in user ? user as unknown as MemberDto : null
  const [member, setMember] = useState<MemberDto | null>(defaultMember)
  const [file, setFile] = useState<File | null>(null)

  const equipmentTypes = getEquipmentTypes({ kind, customQualityControl })
  const equipmentTypeLabel = equipmentTypes
    ? equipmentTypes.map(t => HotLabEquipmentTypeLabels[t]).join(', ')
    : ''

  const fetchEquipment = async (search: string) => {
    const response = await hotLabEquipmentService.getAll({ search, limit: 50, types: equipmentTypes ?? undefined })
    return response?.datas || []
  }

  const isPercent = kind === QCType.CHEMICAL_PURITY || kind === QCType.RADIONUCLIDIC_PURITY
  const isAnalytical = kind === QCType.PH || kind === QCType.CHEMICAL_PURITY || kind === QCType.RADIONUCLIDIC_PURITY
  const isCustomNumeric = kind === QCType.CUSTOM && customQualityControl?.type === CustomQualityControlType.NUMERIC
  const isCustomText = kind === QCType.CUSTOM && customQualityControl?.type === CustomQualityControlType.TEXT
  const isCustomBoolean = kind === QCType.CUSTOM && customQualityControl?.type === CustomQualityControlType.BOOLEAN
  const isCustomList = kind === QCType.CUSTOM && customQualityControl?.type === CustomQualityControlType.LIST

  const visualExpected = kind === QCType.VISUAL ? (visualQualityControl?.label ?? '') : ''

  const { handleSubmit, control, watch, reset, setValue, formState: { isValid, errors } } = useForm<FormValues>({
    resolver: yupResolver(qcTestResultSchema),
    context: { isAnalytical, isPercent, qcType: kind, isCustomNumeric, isCustomBoolean, isCustomList },
    mode: 'onChange',
    defaultValues: {
      observedNumericValue: undefined as unknown as number,
      observedTextValue: '',
      observedBooleanValue: undefined,
      selectedOptionId: '',
      observedDiffers: false,
      measuredAt: new Date(),
      notes: '',
      hotLabEquipmentId: '',
    },
  })

  const fetchMembers = async (search: string) => {
    const response = await MemberService.findAll({ search, limit: 20 })
    return response?.datas || []
  }

  const getMemberLabel = (member: MemberDto | null) => {
    if (!member) {
      return ''
    }
    const roleLabel = MemberRoleEnumLabels[member.role]
    return `${member.firstName} ${member.lastName}${roleLabel ? ` (${roleLabel})` : ''}`
  }

  const watchVisualDiffers = watch('observedDiffers')
  const watchObservedTextValue = watch('observedTextValue')
  const watchNumericValue = watch('observedNumericValue')
  const watchObservedBooleanValue = watch('observedBooleanValue')
  const watchSelectedOptionId = watch('selectedOptionId')

  const visualResult: QCResult = !watchVisualDiffers
    ? QCResult.PASS
    : (watchObservedTextValue ?? '').trim().toLowerCase() === visualExpected.trim().toLowerCase()
      ? QCResult.PASS
      : QCResult.FAIL

  const analyticalResult = isAnalytical
    ? computeAutoResult(kind, String(watchNumericValue ?? ''), {
        phMin: assignment.phMinimum,
        phMax: assignment.phMaximum,
        radiochemicalPurityPercent: assignment.radiochemicalPurityPercent,
        radionuclidicPurityPercent: assignment.radionuclidicPurityPercent,
      })
    : null

  const customNumericResult = isCustomNumeric
    ? computeAutoResult(QCType.CUSTOM, String(watchNumericValue ?? ''), {
        customMin: customQualityControl?.minimum,
        customMax: customQualityControl?.maximum,
      })
    : null

  const customTextExpected = customQualityControl?.expectedValue ?? ''
  const customTextResult: QCResult | null = isCustomText
    ? (!watchVisualDiffers
        ? QCResult.PASS
        : (watchObservedTextValue ?? '').trim().toLowerCase() === customTextExpected.trim().toLowerCase()
          ? QCResult.PASS
          : QCResult.FAIL)
    : null

  const customBooleanResult: QCResult | null = isCustomBoolean && watchObservedBooleanValue != null
    ? (watchObservedBooleanValue === customQualityControl?.expected ? QCResult.PASS : QCResult.FAIL)
    : null

  const customListResult: QCResult | null = isCustomList && watchSelectedOptionId
    ? (watchSelectedOptionId === customQualityControl?.expectedOption?.id ? QCResult.PASS : QCResult.FAIL)
    : null

  const title =
    kind === QCType.VISUAL && visualQualityControl ? VisualQualityControlTypeLabels[visualQualityControl.type]
    : kind === QCType.CUSTOM && customQualityControl ? customQualityControl.label
    : kind === QCType.PH ? 'pH'
    : kind === QCType.CHEMICAL_PURITY ? 'Pureté radiochimique'
    : kind === QCType.RADIONUCLIDIC_PURITY ? 'Pureté radionuclidique'
    : 'N/A'

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  const handleClose = () => {
    reset()
    setMember(defaultMember)
    setFile(null)
    onClose()
  }

  const onFormSubmit = async (data: FormValues) => {
    const dto: CreateQcTestResultDto = {
      type: kind,
      measuredByMemberId: member?.id,
      measuredAt: data.measuredAt,
      notes: data.notes || undefined,
      hotLabEquipmentId: data.hotLabEquipmentId
    } as CreateQcTestResultDto

    if (kind === QCType.VISUAL) {
      dto.observedTextValue = data.observedDiffers ? data.observedTextValue : visualExpected
      dto.visualQualityControlId = visualQualityControl?.id
    } else if (isAnalytical) {
      dto.observedNumericValue = data.observedNumericValue
    } else if (kind === QCType.CUSTOM) {
      dto.customQualityControlId = customQualityControl?.id
      if (isCustomNumeric) {
        dto.observedNumericValue = data.observedNumericValue
      } else if (isCustomText) {
        dto.observedTextValue = data.observedDiffers ? data.observedTextValue : customTextExpected
      } else if (isCustomBoolean) {
        dto.observedBooleanValue = data.observedBooleanValue ?? undefined
      } else if (isCustomList) {
        const selected = customQualityControl?.options.find(o => o.id === data.selectedOptionId)
        dto.observedTextValue = selected?.label ?? data.selectedOptionId
      }
    }

    const action =
      entityType === 'preparationLot'
        ? savePreparationLotQcTestResult
        : saveGeneratorElutionQcTestResult

    try {
      await dispatch(action({ entityId, dto, file: file ?? undefined })).unwrap()
      handleClose()
    } catch (e) {
      cb('Une erreur est survenue lors de l\'enregistrement du résultat')
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth TransitionComponent={UpTransition}>
      <DialogContent className="p-0">
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <div className="flex flex-col gap-4 p-6">
            <FormHeader
              title={title}
              subTitles={[`Valeur attendue : ${getQualityControlExpectedLabel({
                kind, assignment, visualQualityControl: visualQualityControl ?? undefined, customQualityControl: customQualityControl ?? undefined
              })}`]}
              onClose={handleClose}
              isSubmitting={submitting}
              submitText="Enregistrer"
              canSubmit={isValid}
            />

            <Box display="flex" flexDirection="column" gap={2.5}>
              {kind === QCType.VISUAL && (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ResultChip result={visualResult} />
                  </Box>
                  <FormControlLabel
                    control={
                      <Controller
                        name="observedDiffers"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            checked={field.value}
                            onChange={(e) => {
                              field.onChange(e.target.checked)
                              if (!e.target.checked) {
                                setValue('observedTextValue', '')
                              }
                            }}
                            size="small"
                          />
                        )}
                      />
                    }
                    label={<Typography variant="body2">{`Résultat différent de "${visualExpected}"`}</Typography>}
                  />
                  {watchVisualDiffers && (
                    <InputField
                      control={control}
                      name="observedTextValue"
                      label="Valeur observée"
                      size="small"
                      fullWidth
                      autoFocus
                      helperText={
                        watchObservedTextValue?.trim().toLowerCase() === visualExpected.trim().toLowerCase() && watchObservedTextValue
                          ? 'Correspond à la valeur attendue → Conforme'
                          : undefined
                      }
                    />
                  )}
                </Box>
              )}

              {isAnalytical && (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <InputField
                    control={control}
                    name="observedNumericValue"
                    type="number"
                    label="Valeur mesurée"
                    size="small"
                    preventNegative={isAnalytical}
                    fullWidth
                    autoFocus
                    inputProps={{
                        step: isAnalytical ? 0.01 : 'any',
                      }}
                    InputProps={{
                      endAdornment: isPercent
                        ? <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5, whiteSpace: 'nowrap' }}>%</Typography>
                        : undefined,
                    }}
                  />
                  {watchNumericValue != null && !isNaN(watchNumericValue) && analyticalResult && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.secondary">Résultat :</Typography>
                      <ResultChip result={analyticalResult} />
                    </Box>
                  )}
                </Box>
              )}

              {isCustomNumeric && (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <InputField
                    control={control}
                    name="observedNumericValue"
                    type="number"
                    label="Valeur mesurée"
                    size="small"
                    fullWidth
                    autoFocus
                    inputProps={{ step: 'any' }}
                    InputProps={{
                      endAdornment: customQualityControl?.unit
                        ? <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5, whiteSpace: 'nowrap' }}>{customQualityControl.unit}</Typography>
                        : undefined,
                    }}
                  />
                  {watchNumericValue != null && !isNaN(watchNumericValue) && customNumericResult && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.secondary">Résultat :</Typography>
                      <ResultChip result={customNumericResult} />
                    </Box>
                  )}
                </Box>
              )}

              {isCustomText && (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ResultChip result={customTextResult} />
                  </Box>
                  <FormControlLabel
                    control={
                      <Controller
                        name="observedDiffers"
                        control={control}
                        render={({ field }) => (
                          <Checkbox
                            checked={field.value}
                            onChange={(e) => {
                              field.onChange(e.target.checked)
                              if (!e.target.checked) {
                                setValue('observedTextValue', '')
                              }
                            }}
                            size="small"
                          />
                        )}
                      />
                    }
                    label={<Typography variant="body2">{`Résultat différent de "${customTextExpected}"`}</Typography>}
                  />
                  {watchVisualDiffers && (
                    <InputField
                      control={control}
                      name="observedTextValue"
                      label="Valeur observée"
                      size="small"
                      fullWidth
                      autoFocus
                      helperText={
                        watchObservedTextValue?.trim().toLowerCase() === customTextExpected.trim().toLowerCase() && watchObservedTextValue
                          ? 'Correspond à la valeur attendue → Conforme'
                          : undefined
                      }
                    />
                  )}
                </Box>
              )}

              {isCustomBoolean && (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <Controller
                    name="observedBooleanValue"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        row
                        value={field.value == null ? '' : String(field.value)}
                        onChange={(e) => field.onChange(e.target.value === 'true')}
                      >
                        <FormControlLabel value="true" control={<Radio size="small" />} label="Oui" />
                        <FormControlLabel value="false" control={<Radio size="small" />} label="Non" />
                      </RadioGroup>
                    )}
                  />
                  {watchObservedBooleanValue != null && customBooleanResult && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.secondary">Résultat :</Typography>
                      <ResultChip result={customBooleanResult} />
                    </Box>
                  )}
                </Box>
              )}

              {isCustomList && (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <SelectField
                    name="selectedOptionId"
                    control={control}
                    label="Option observée"
                    size="small"
                    displayEmpty
                    options={customQualityControl?.options.map(o => ({ label: o.label, value: o.id })) ?? []}
                  />
                  {watchSelectedOptionId && customListResult && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.secondary">Résultat :</Typography>
                      <ResultChip result={customListResult} />
                    </Box>
                  )}
                </Box>
              )}

              <Box display="flex" gap={1} alignItems="stretch">
                <Box flex={1}>
                  <Controller
                    name="measuredAt"
                    control={control}
                    render={({ field }) => (
                      <DateTimePicker
                        label="Date / heure mesure"
                        value={field.value ? moment(field.value) : null}
                        onChange={(date) => field.onChange(date ? date.toDate() : null)}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                          },
                        }}
                        disableFuture
                      />
                    )}
                  />
                </Box>

                <Box flex={1}>
                  <ServerAutocomplete<MemberDto>
                    width="100%"
                    value={member}
                    defaultValue={defaultMember}
                    initialOptions={defaultMember ? [defaultMember] : []}
                    fetchService={fetchMembers}
                    onChange={(val: MemberDto | null) => setMember(val)}
                    getValue={(val) => val?.id?.toString() ?? null}
                    getOptionLabel={(val) => getMemberLabel(val as MemberDto)}
                    debounceMs={300}
                    placeholder="Rechercher un membre..."
                    label="Agent"
                    minSearchLength={0}
                  />
                </Box>
              </Box>

              {(equipmentTypes?.length ?? 0) > 0 && (
                <Box>
                  <AddWrapperInput
                    legend={'Matériel de laboratoire'}
                    addTooltipTitle="Ajouter du matériel"
                    dialogContent={(close) => (
                      <HotLabEquipmentForm
                        onClose={() => {
                          close()
                        }}
                      />
                    )}
                    input={
                      <ServerAutocomplete<HotLabEquipmentDto>
                        autoFetchOnMount
                        fetchService={fetchEquipment}
                        onChange={(value: HotLabEquipmentDto | null) => {
                          setValue('hotLabEquipmentId', value?.id ?? '')
                        }}
                        getValue={(value) => value?.id?.toString() || null}
                        getOptionLabel={(value) => {
                          const equipment = value as HotLabEquipmentDto
                          return equipment ? equipment.name : ''
                        }}
                        debounceMs={300}
                        placeholder={'Rechercher un matériel de laboratoire...'}
                        label={'Matériel de laboratoire'}
                        helperText={errors.hotLabEquipmentId?.message}
                        error={!!errors.hotLabEquipmentId}
                        minSearchLength={0}
                      />
                    }
                    />
                    <Alert severity="info">
                      Parmi : {equipmentTypeLabel}
                    </Alert>
                  </Box>
              )}

              <InputField
                control={control}
                name="notes"
                label="Commentaire"
                size="small"
                fullWidth
                multiline
                rows={2}
              />

              <SimpleFileInput file={file} onSelect={setFile} />
            </Box>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
